import { MantineColor } from '@mantine/core'
import {
  EndscreenConfigApiDTO,
  EndscreenElementConfigApiDTO,
  EndscreenElementPositionApiDTO,
  PromotionOperationStatusApiDTO,
} from '../codegen/generated/Api.schemas'
import {
  IPromotionEndscreenSlotChoice,
  IPromotionInfoCard,
  NewPromotionLayoutId,
} from '../types/components/VideoPromotion.type'

type IPromotionEndscreenLayout = {
  id: NewPromotionLayoutId
  slots: EndscreenElementPositionApiDTO[]
}

// positions are fractions of the 16:9 video frame; the last slot is always the subscribe button.
// Each layout follows YouTube Studio's endscreen rules (checked in VideoPromotion.helper.test.ts)
const VIDEO_WIDTH = 0.322807
const SUBSCRIBE_WIDTH = 0.15438597

const YOUTUBE_VIDEO_ID = /^[\w-]{11}$/
const YOUTUBE_VIDEO_PATH = /^\/(?:shorts|embed|live)\/([\w-]{11})/

const LAYOUTS: IPromotionEndscreenLayout[] = [
  {
    id: 'A',
    slots: [
      { left: 0.08, top: 0.18, width: 0.38 },
      { left: 0.69, top: 0.33, width: SUBSCRIBE_WIDTH },
    ],
  },
  {
    id: 'B',
    slots: [
      { left: 0.06, top: 0.16, width: VIDEO_WIDTH },
      { left: 0.413, top: 0.16, width: VIDEO_WIDTH },
      { left: 0.77, top: 0.32, width: SUBSCRIBE_WIDTH },
    ],
  },
  {
    id: 'C',
    slots: [
      { left: 0.145, top: 0.1308, width: 0.34 },
      { left: 0.145, top: 0.5008, width: 0.34 },
      { left: 0.515, top: 0.1308, width: 0.34 },
      { left: 0.608, top: 0.5336, width: SUBSCRIBE_WIDTH },
    ],
  },
]

export const VideoPromotionHelper = {
  NEW_OPERATION: 'new',
  LAYOUTS,
  STATUSES: [
    { id: 'pending', color: 'gray' },
    { id: 'running', color: 'blue' },
    { id: 'completed', color: 'green' },
    { id: 'failed', color: 'red' },
  ] satisfies { id: PromotionOperationStatusApiDTO; color: MantineColor }[],
  DEFAULT_INFOCARD: {
    target: {
      type: 'video',
      targetId: '',
    },
    customMessage: '',
    teaserText: '',
    teaserStartOffsetSeconds: '',
  } satisfies Omit<IPromotionInfoCard, 'id'>,
  INFOCARDS_TYPES: ['video', 'playlist'],
  /** A bare video id, or the id found in a watch, youtu.be, shorts, embed or live URL */
  parseYoutubeVideoId: (input: string): string | undefined => {
    const value = input.trim()
    if (YOUTUBE_VIDEO_ID.test(value)) {
      return value
    }
    let url: URL
    try {
      url = new URL(value.includes('://') ? value : `https://${value}`)
    } catch {
      return undefined
    }
    const host = url.hostname.replace(/^(www|m)\./, '')
    const candidate =
      host === 'youtu.be'
        ? url.pathname.slice(1)
        : host === 'youtube.com'
          ? url.searchParams.get('v') ?? url.pathname.match(YOUTUBE_VIDEO_PATH)?.[1]
          : undefined
    return candidate && YOUTUBE_VIDEO_ID.test(candidate) ? candidate : undefined
  },
  getStatus: (statusId: PromotionOperationStatusApiDTO) => {
    const status = VideoPromotionHelper.STATUSES.find(s => s.id === statusId)

    if (status) {
      return status
    }

    return { id: 'unknown', color: 'dark' }
  },
  buildEndscreenConfig: (
    layout: IPromotionEndscreenLayout,
    choices: IPromotionEndscreenSlotChoice[],
    startSecondsBeforeEnd: number,
    durationSeconds: number | undefined,
  ): EndscreenConfigApiDTO => {
    const elements = layout.slots
      .map((position, index): EndscreenElementConfigApiDTO | null => {
        if (index === layout.slots.length - 1) {
          return { position, type: 'subscribe' }
        }

        const choice = choices[index]
        if (!choice) {
          return null
        }

        switch (choice.type) {
          case 'videoFixed':
            return { position, type: 'video', videoType: 'FIXED', videoId: choice.videoId }
          case 'recentUpload':
            return { position, type: 'video', videoType: 'RECENT_UPLOAD' }
          case 'bestForViewer':
            return { position, type: 'video', videoType: 'BEST_FOR_VIEWER' }
          case 'playlist':
            return { position, type: 'playlist', playlistId: choice.playlistId ?? '' }
          case 'channel':
            return {
              position,
              type: 'channel',
              channelId: choice.channelId ?? '',
              customMessage: '',
            }
        }
      })
      .filter((element): element is EndscreenElementConfigApiDTO => element !== null)

    return {
      mode: 'custom',
      startSecondsBeforeEnd,
      durationSeconds,
      elements,
    }
  },
}
